import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import accountApi from '../../services/accountApi';

function AccountSettingsPage() {
  const { user, refreshSession } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [inviteToken, setInviteToken] = useState('');
  const [acceptingInvite, setAcceptingInvite] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
    }
  }, [user]);

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await accountApi.updateAccount({ first_name: firstName, last_name: lastName });
      await refreshSession();
      setSuccess('Profile updated.');
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setChangingPassword(true);
    setError(null);
    setSuccess(null);
    try {
      await accountApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setSuccess('Password changed.');
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAcceptInvite = async (event) => {
    event.preventDefault();
    setAcceptingInvite(true);
    setError(null);
    setSuccess(null);
    try {
      await accountApi.acceptInvitation(inviteToken.trim());
      setInviteToken('');
      await refreshSession();
      setSuccess('Invitation accepted. You can switch to the new workspace.');
    } catch (err) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setAcceptingInvite(false);
    }
  };

  return (
    <div>
      <h1 className="product-page-title">Account Settings</h1>
      <p className="product-page-subtitle">Manage your personal profile and credentials.</p>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="product-card">
        <h2 className="h6 mb-3">Profile</h2>
        <Form onSubmit={handleSaveProfile}>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control type="email" value={user?.email || ''} readOnly disabled />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>First name</Form.Label>
            <Form.Control
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Last name</Form.Label>
            <Form.Control value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Form.Group>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              'Save profile'
            )}
          </Button>
        </Form>
      </div>

      <div className="product-card">
        <h2 className="h6 mb-3">Change password</h2>
        <Form onSubmit={handleChangePassword}>
          <Form.Group className="mb-3">
            <Form.Label>Current password</Form.Label>
            <Form.Control
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>New password</Form.Label>
            <Form.Control
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </Form.Group>
          <Button type="submit" disabled={changingPassword}>
            {changingPassword ? 'Updating…' : 'Change password'}
          </Button>
        </Form>
      </div>

      <div className="product-card">
        <h2 className="h6 mb-3">Accept invitation</h2>
        <p className="text-muted small">
          Paste an invitation token received by email (or the development token when
          DEBUG email delivery is unavailable).
        </p>
        <Form onSubmit={handleAcceptInvite}>
          <Form.Group className="mb-3">
            <Form.Label>Invitation token</Form.Label>
            <Form.Control
              value={inviteToken}
              onChange={(e) => setInviteToken(e.target.value)}
              required
            />
          </Form.Group>
          <Button type="submit" disabled={acceptingInvite}>
            {acceptingInvite ? 'Accepting…' : 'Accept invitation'}
          </Button>
        </Form>
      </div>
    </div>
  );
}

export default AccountSettingsPage;
